import { Component, ElementRef, HostListener, Input, OnInit, QueryList, SecurityContext, ViewChild, ViewChildren } from '@angular/core';
import { GridService } from 'app/_services/grid.service';
import { debounceTime, Subject } from 'rxjs';
import { AppComponent } from '../app.component';
import { IGridData } from '../_models/IGridData';
import { IFilter } from '../_models/IFilter';
import { IHeader } from '../_models/IHeader';
import { ILine } from '../_models/ILine';
import { FilterController } from 'app/_controllers/filterController';
import { GridController } from 'app/_controllers/gridController';
import { SelectExpandController } from 'app/_controllers/selectExpandController';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { CommunicationService } from 'app/_services/communication.service';
import { DetailGridComponent } from 'app/detail-grid-component/detail-grid.component';
import { ISettings } from 'app/_models/ISettings';
import {
  IconDefinition, faDownLeftAndUpRightToCenter, faFilter, faTableColumns, faAnglesLeft, faFileExport,
  faChevronLeft, faAnglesRight, faChevronRight, faArrowUpLong, faArrowDownLong, faArrowUpRightFromSquare,
  faCheck, faXmark, faArrowUpWideShort, faCircle, faChevronDown, faPlus, faAngleDoubleUp, faInfoCircle, faCopy, faSquareXmark,
  faPaste, faEraser, faCircleDot, faRemove, faEyeSlash, faUpDown, faAngleDoubleDown, faAngleDown, faAngleUp, faSquareCheck,
  faChartSimple, faCaretDown, faCaretUp, faTriangleExclamation, faGripVertical, faSquare, faBorderAll, faTableCells, faNetworkWired
} from '@fortawesome/free-solid-svg-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IWidget } from 'app/_models/IWidget';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.css']
})
export class GridComponent implements OnInit {
  //#region Global Fields  
  @Input() settings: ISettings;
  @Input() userId: string;
  @Input() username: string;
  @Input() mobileView: boolean;
  @Input() nodeId: string;
  @Input() mainGridName: string;
  compactGrid: boolean = false;
  serverDateFormat: string = 'MM/dd/yyyy';
  shiftSelect: boolean = false;
  controlSelect: boolean = false;
  headers: IHeader[] = [];
  lines: ILine[] = [];
  selectedLines: ISelectedLine[] = [];
  expandedRows: number[] = [];
  viewName: string = 'master';
  reloadingGridFromError: boolean = false;
  //#endregion

  //#region Pagination Fields  
  pageSize: number = 20;
  totalRecords: number = 0;
  pageCount: number = 1;
  offset: number = 0;
  currentpage: number = 1;
  rowCountFrom: number;
  rowCountTo: number;
  //#endregion

  //#region Grid Layout Fields  
  colWidths: string[] = [];
  prevColWidths: string[] = [];
  reorderedHeaders: IHeader[] = [];
  saveUpdatedLayout: boolean = false;
  //#endregion

  //#region Sort & Filter Fields
  sortChanged: Subject<string> = new Subject<string>();
  filterUpdated: Subject<string> = new Subject<string>();
  sortOrder: boolean = true;
  sortString: string = '';
  showFilters: boolean = false;
  filterString: string = '';
  filters: IFilter[] = [];
  activeFilterCount: number = 0;
  showMobileFilters: boolean = false;
  showMobileSort: boolean = false;
  showAdvancedFiltersButton: boolean = false;
  editableFilterString: string = '';
  editableFilterStringError: string = '';
  filteredByEditableFilterString: boolean = false;
  showAdvancedFilters: boolean = false;
  filteredFromHistory: boolean = false;
  filteredByBlur: boolean = false;
  //#endregion

  //#region Misc Fields 
  @ViewChild(DetailGridComponent) detailGrid: DetailGridComponent;
  @ViewChild('tabelDiv') tabelDiv!: ElementRef<HTMLDivElement>;
  showNoRecordsFoundLabel: boolean = false;
  hasChildren: boolean = false;
  loadedFromDirectLink: boolean = false;
  zoomedDetailView: boolean = false;
  contextMenuVisible: boolean = false;
  contextMenuTop: number = 0;
  contextMenuLeft: number = 0;
  contextMenuType: string;
  contextMenuObject: IFilter | IHeader | ILine;
  selectedText: string;
  scrollX: number = 0;
  scrollY: number;
  updateScroll: boolean = true;
  longPressTimer: any;
  refreshedFromDetail: boolean = false;
  directModeEnabled: boolean = false;
  i: number = 0;
  widgetVisible: boolean = false;
  widgetMax: boolean = false;
  widgets: IWidget[] = [];
  mobileTileSize: string = 'Medium';
  //#endregion      

  //#region Icons
  faDownLeftAndUpRightToCenter: IconDefinition = faDownLeftAndUpRightToCenter;
  faFilter: IconDefinition = faFilter;
  faTableColumns: IconDefinition = faTableColumns;
  faAnglesLeft: IconDefinition = faAnglesLeft;
  faChevronLeft: IconDefinition = faChevronLeft;
  faChevronRight: IconDefinition = faChevronRight;
  faAnglesRight: IconDefinition = faAnglesRight;
  faArrowUpLong: IconDefinition = faArrowUpLong;
  faArrowDownLong: IconDefinition = faArrowDownLong;
  faCheck: IconDefinition = faCheck;
  faXmark: IconDefinition = faXmark;
  faArrowUpWideShort: IconDefinition = faArrowUpWideShort;
  faCircle: IconDefinition = faCircle;
  faChevronDown: IconDefinition = faChevronDown;
  faPlus: IconDefinition = faPlus;
  faAngleDoubleUp: IconDefinition = faAngleDoubleUp;
  faInfoCircle: IconDefinition = faInfoCircle;
  faArrowUpRightFromSquare: IconDefinition = faArrowUpRightFromSquare;
  faFileExport: IconDefinition = faFileExport;
  faCopy: IconDefinition = faCopy;
  faPaste: IconDefinition = faPaste;
  faEraser: IconDefinition = faEraser;
  faCircleDot: IconDefinition = faCircleDot;
  faRemove: IconDefinition = faRemove;
  faEyeSlash: IconDefinition = faEyeSlash;
  faUpDown: IconDefinition = faUpDown;
  faAngleDown: IconDefinition = faAngleDown;
  faAngleDoubleDown: IconDefinition = faAngleDoubleDown;
  faAngleUp: IconDefinition = faAngleUp;
  faSquareCheck: IconDefinition = faSquareCheck;
  faSquareXmark: IconDefinition = faSquareXmark;
  faChartSimple: IconDefinition = faChartSimple;
  faCaretDown: IconDefinition = faCaretDown;
  faCaretUp: IconDefinition = faCaretUp;
  faTriangleExclamation: IconDefinition = faTriangleExclamation;
  faGripVertical: IconDefinition = faGripVertical;
  faSquare: IconDefinition = faSquare;
  faBorderAll: IconDefinition = faBorderAll;
  faTableCells: IconDefinition = faTableCells;
  faNetworkWired: IconDefinition = faNetworkWired;
  //#endregion

  constructor(
    public app: AppComponent,
    public gridService: GridService,
    private communicationService: CommunicationService,
    public filterController: FilterController,
    public gridController: GridController,
    public selectExpandController: SelectExpandController,
    private sanitizer: DomSanitizer
  ) {
    this.communicationService.base_grid_loadGrid$.subscribe((nodeId) => {
      this.nodeId = nodeId;
      this.widgetVisible = this.settings.widgetsOpen;
      this.widgetMax = false;
      this.loadGrid();
    });

    this.communicationService.detailgrid_grid_deselectParentLines$.subscribe(() => {
      this.deselectAll();
    });

    this.communicationService.detailgrid_grid_updateChildPinState$.subscribe(tab => {
      this.lines.forEach(line => {
        line.children.forEach(child => {
          if (child.tableIndex == tab.tableIndex) {
            child.pinned = tab.pinned;
            child.showPin = tab.showPin;
          } else {
            child.pinned = false;
            child.showPin = false;
          }
        })
      })
    });

    this.communicationService.detail_grid_grid_zoomIntoDetailView$.subscribe(() => {
      this.zoomedDetailView = !this.zoomedDetailView;
    });

    this.communicationService.widget_grid_toggleMaxWidgets$.subscribe(() => {
      this.widgetMax = !this.widgetMax;
    });

    this.filterUpdated.pipe(debounceTime(1250)).subscribe(() => {
      if (this.settings.filterDelaySetting) this.buildFilterString();
    });

    this.sortChanged.pipe(debounceTime(1000)).subscribe(() => {
      this.getData();
    });
  }

  ngOnInit(): void {
    this.compactGrid = this.settings.compactGridSetting;
    this.widgetVisible = this.settings.widgetsOpen;
    
    if (!localStorage.getItem('qx_direct') && !localStorage.getItem('qx_directFromExisting')) {
      this.loadGrid();
      return;
    }

    if (localStorage.getItem('qx_directFromExisting')) {
      this.communicationService.loadRadButtons('master', this.nodeId);
      localStorage.removeItem('qx_directFromExisting');
      this.getData();
      return;
    }

    this.directModeEnabled = true;
    this.loadedFromDirectLink = true;
    this.communicationService.loadRadButtons('master', this.nodeId);
    let lineId = JSON.parse(localStorage.getItem('qx_direct')).lineId;
    if (lineId) {
      this.getPageNumber(lineId);
      return;
    }

    this.getData();
  }

  loadGrid(): void {
    this.showMobileDetailView = false;
    this.mobileDetailViewLine = undefined;
    this.showFilters = false;
    this.sortString = '';
    this.filterString = '';
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    this.showAdvancedFilters = false;
    this.offset = 0;
    this.currentpage = 1;
    this.pageSize = 20;
    this.headers = [];
    this.lines = [];
    this.filters = [];
    this.selectedLines = [];
    this.loadedFromDirectLink = false;
    this.showMobileFilters = false;
    this.showMobileSort = false;
    this.reloadingGridFromError = false;
    this.unFocusAllFilters();
    this.getData();
  }

  setupGridLoad(): FormData {
    if (this.filteredFromHistory && !this.reloadingGridFromError) this.app.showLoader();
    if (!this.directModeEnabled && localStorage.getItem(`qx_filters_advanced_${this.nodeId}`))
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}`)).serverString;

    if (!this.directModeEnabled && localStorage.getItem(`qx_filters_${this.nodeId}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}`)).filterString;

    if (!this.directModeEnabled && localStorage.getItem(`qx_sort_${this.nodeId}`) && !this.reloadingGridFromError)
      this.sortString = JSON.parse(localStorage.getItem(`qx_sort_${this.nodeId}`)).sortString;

    if (this.directModeEnabled && sessionStorage.getItem(`qx_filters_${this.nodeId}`) && !this.reloadingGridFromError)
      this.filterString = JSON.parse(sessionStorage.getItem(`qx_filters_${this.nodeId}`)).filterString;

    if (this.directModeEnabled && sessionStorage.getItem(`qx_sort_${this.nodeId}`) && !this.reloadingGridFromError)
      this.sortString = JSON.parse(sessionStorage.getItem(`qx_sort_${this.nodeId}`)).sortString;

    this.showNoRecordsFoundLabel = false;
    let keys = ['nodeId', 'viewName', 'sortString', 'filterString', 'pageSize', 'keyValue',
      'offset', 'username', 'userId', 'advancedFilterString', 'reloadingGridFromError', 'freeColumnResizingEnabled'];
    let values = [this.nodeId, 'master', this.sortString, this.filterString, this.pageSize.toString(), this.nodeId,
    this.offset.toString(), this.username, this.userId, this.filteredByEditableFilterString.toString(),
    this.reloadingGridFromError.toString(), this.settings.freeColumnResizeSetting.toString()];
    return this.app.buildForm(keys, values);
  }

  getData(): void {
    this.gridService.getData(this.setupGridLoad()).subscribe({
      next: (data: IGridData) => {
        this.reloadingGridFromError = false;
        this.serverDateFormat = data.dateFormat;
        this.headers = data.headers;
        
        if (!this.directModeEnabled && localStorage.getItem(`qx_filters_advanced_${this.nodeId}`)) {
          this.editableFilterString = JSON.parse(localStorage.getItem(`qx_filters_advanced_${this.nodeId}`)).userString;
          this.showAdvancedFilters = true;
          this.showFilters = false;
          this.filters = this.filterController.clearAllFilters(this.filters);
        }

        if (!this.directModeEnabled && localStorage.getItem(`qx_filters_${this.nodeId}`)) {
          this.filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}`)).filters;
          if (this.filters.filter(filter => filter.isActive).length > 0) {
            this.showFilters = this.filterString.length > 0 ? true : false;
            if (this.showFilters) this.refocusFilters();
          }
        }

        if (this.directModeEnabled && sessionStorage.getItem(`qx_filters_${this.nodeId}`)) {
          this.filters = JSON.parse(sessionStorage.getItem(`qx_filters_${this.nodeId}`)).filters;
          this.showFilters = this.filterString.length > 0 ? true : false;
          if (this.showFilters) this.refocusFilters();
        }

        if (!this.directModeEnabled && this.filters.length == 0)
          this.filters = this.filterController.loadFilters(this.headers);

        let result = this.gridController.formatSanitise(data.lines, this.headers, this.filters, this.settings.dateFormatSetting, this.nodeId, 'master', this.directModeEnabled);
        this.headers = result.headers;
        this.lines = result.lines;
        this.filters = result.filters;
        this.communicationService.updateRadHeaders(this.headers, this.serverDateFormat);
        
        if (this.mobileView) this.activeFilterCount = this.filters.filter(filter => filter.isActive).length;
        if (data.lines.length == 0) {
          this.lines = [];
          this.showNoRecordsFoundLabel = true;
          this.totalRecords = 0;
          this.rowCountFrom = 0;
          this.rowCountTo = 0;
          this.app.hideLoader();
          localStorage.removeItem('qx_direct');
          return;
        }

        if (!this.directModeEnabled && localStorage.getItem(`qx_filters_${this.nodeId}`)) {
          if (this.filters.filter(filter => filter.isActive).length == 0) {
            localStorage.removeItem(`qx_filters_${this.nodeId}`);
            this.filterString = '';
          } else {
            localStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
          }
        }

        if (this.loadedFromDirectLink) {
          let lineId = JSON.parse(localStorage.getItem('qx_direct')).lineId;
          if (lineId) {
            let line = this.lines.find(line => line.rowId == lineId);
            if (line) {
              this.selectedLines.push({ id: lineId, lineData: line.list, lineNumber: line.lineNumber });
              if (this.mobileView) {
                setTimeout(() => {
                  let element = document.getElementById('tile-' + lineId);
                  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 500);
              }
            }
          }

          this.loadedFromDirectLink = false;
          localStorage.removeItem('qx_direct');
        }

        this.hasChildren = data.hasChildren;
        this.totalRecords = data.totalRecords;
        this.viewName = data.masterViewName;
        this.calculatePageCount();
        this.reExpandRows();
        this.reSelectRows();
        this.colWidths = [];
        this.reorderedHeaders = [];
        this.app.hideLoader();
        sessionStorage.setItem('isMaster', 'true');
        this.rowCountFrom = this.currentpage > 1 ? this.offset : 1;
        this.rowCountTo = this.currentpage > 1 ? this.offset + this.lines.length : this.lines.length;
        this.filteredFromHistory = false;
        this.getHeaderCalculationsFromStorage();
        setTimeout(() => {
          this.updateScroll = true;
          if (this.tabelDiv) {
            this.tabelDiv.nativeElement.scrollTop = this.scrollY;
            this.tabelDiv.nativeElement.scrollLeft = this.scrollX;
          }
        });
        
        this.sortOrder = this.sortString && this.sortString.includes('asc') ? true : false;
        this.communicationService.filterRadButtons(this.viewName, []);
      },
      error: (errorLog) => {
        this.gridController.handleGridError(errorLog, this.nodeId, 'master', undefined, this.settings);
        this.reloadingGridFromError = true;
        this.loadGrid();
      }
    });
  }

  //#region Grid Layout
  toggleCompactGrid(): void {
    this.filters.forEach(f => f.focused = false);
    this.compactGrid = !this.compactGrid;
    if (this.detailGrid) {
      this.detailGrid.compactGrid = this.compactGrid;
      if (this.detailGrid.detailGrid) this.detailGrid.detailGrid.compactGrid = this.compactGrid;
    }
  }

  @ViewChild('tablerow', { static: false }) rowHeader!: any;
  handleColResize(): void {
    if (this.directModeEnabled) {
      this.app.alertError(this.app.translations.ALERT_Error_DirectModeGridEdit);
      return;
    }

    let visibleHeaders = this.headers.filter(header => header.visible);
    let children = this.rowHeader.nativeElement.parentNode.children;
    for (let index = 0; index < children.length; index++) {
      let resizedHeader = visibleHeaders.find(header => children[index].innerText.trim().includes(header.caption));
      if (resizedHeader) resizedHeader.width = `${children[index].clientWidth}px`;
    }

    this.gridController.saveLayout(this.nodeId, 'master', this.username, this.headers).then(() => { });
  }

  handleColReorder(): void {
    if (this.directModeEnabled) {
      this.app.alertError(this.app.translations.ALERT_Error_DirectModeGridEdit);
      return;
    }

    this.gridController.saveLayout(this.nodeId, 'master', this.username, this.headers).then(() => { });
  }

  hideColumn(header: IHeader): void {
    let visibleCount = this.headers.filter(header => header.visible).length;
    if (visibleCount == 1) {
      this.app.alertError(this.app.translations.ALERT_Error_AllHeadersHidden);
      return;
    };

    if (this.directModeEnabled) {
      this.app.alertError(this.app.translations.ALERT_Error_DirectModeGridEdit);
      return;
    }

    header.visible = false;
    if (this.filters.length > 0) {
      let filterIndex = this.filters.findIndex(filter => filter.field == header.field);
      if (filterIndex != -1) this.filters.splice(filterIndex, 1);
      let filterString = this.filterController.buildFilterString(this.filters);
      localStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: filterString, filters: this.filters }));
      this.getData();
    }

    this.gridController.saveLayout(this.nodeId, 'master', this.username, this.headers).then(() => { });
  }
  //#endregion

  //#region Sort & Filter
  sortFromMobile(): void {
    this.showMobileSort = false;
    let header = this.headers.find(header => header.sortedBy);
    if (header) {
      this.sortString = this.sortOrder ? `${header.field} asc` : `${header.field} desc`;
      localStorage.setItem(`qx_sort_${this.nodeId}`, JSON.stringify({ header, sortString: this.sortString }));
    }
    this.getData();
  }

  updateSortByColumnMobile(header: IHeader): void {
    if (header.displayFormat == 'IMAGE' || header.dataType == 'Byte[]') return;
    localStorage.removeItem(`qx_sort_${this.nodeId}`);
    header.sortedBy = !header.sortedBy;
    if (header.sortedBy) this.sortOrder = true;
  }

  sort(header: IHeader): void {
    this.headers.forEach(header => header.sortedBy = false);

    if (!header) {
      if (!this.directModeEnabled)
        localStorage.removeItem(`qx_sort_${this.nodeId}`);
      else
        sessionStorage.removeItem(`qx_sort_${this.nodeId}`);
      this.sortString = '';
      this.sortOrder = true;
      this.getData();
      return;
    }

    if (header.displayFormat == 'IMAGE' || header.dataType == 'Byte[]') return;
    header.sortedBy = !header.sortedBy;
    if (header.sortedBy) this.sortOrder = !this.sortOrder;
    this.sortString = this.sortOrder ? `${header.field} asc` : `${header.field} desc`;
    if (!this.directModeEnabled)
      localStorage.setItem(`qx_sort_${this.nodeId}`, JSON.stringify({ header, sortString: this.sortString }));
    else
      sessionStorage.setItem(`qx_sort_${this.nodeId}`, JSON.stringify({ header, sortString: this.sortString }));
    this.getData();
  }

  updateValue(value: string, filter: IFilter, event: any): void {
    filter.isActive = true;
    filter.isNull = false;
    filter.updated = true;
    filter.value = value == undefined ? event.checked : value;
    if (filter.value.length == 0) {
      filter.isActive = false;
      if (this.filters.filter(f => f.isActive).length == 0) {
        this.pageSize = 20;
        this.filters = this.filterController.clearAllFilters(this.filters);
        this.filterString = '';
        if (!this.directModeEnabled) localStorage.removeItem(`qx_filters_${this.nodeId}`);
        else sessionStorage.removeItem(`qx_filters_${this.nodeId}`);
        this.getData();
        return;
      }
    }

    if (this.mobileView) return;
    if (filter.displayFormat == 'CHECK') {
      this.buildFilterString();
      return;
    }

    if (filter.value && filter.history) {
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(filter.value.toLowerCase())
      );
    } else filter.filteredHistory = [...filter.history];

    if (this.settings.filterDelaySetting && this.filters.filter(f => f.isActive).length > 0) this.filterUpdated.next('');
    else {
      this.unFocusAllFilters();
      this.filteredByBlur = true;
    }
  }

  updateFilterFromHistory(value: string, filter: IFilter): void {
    filter.isActive = true;
    filter.focused = false;
    filter.isNull = false;
    filter.updated = true;
    filter.value = value;
    filter.showHistory = false;

    if (filter.value && filter.history) {
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(filter.value.toLowerCase())
      );
    } else filter.filteredHistory = [...filter.history];

    this.buildFilterString();
  }

  buildFilterString(): void {
    let filter = this.filters.find(filter => filter.updated);
    if (filter) {
      if (filter.displayFormat != 'CHECK' && filter.history.length >= 0 &&
        !filter.history.find(historyVal => historyVal == filter.value) && filter.value.length > 0) {
        filter.history.unshift(filter.value);
        if (filter.history.length > 5) filter.history.pop();
      } else if (filter.displayFormat == 'CHECK') {
        let focusedFilter = this.filters.find(filter => filter.focused);
        if (focusedFilter) focusedFilter.focused = false;
      }

      this.filterString = this.filterController.buildFilterString(this.filters);
      filter.updated = false;
      this.editableFilterString = '';
      this.filteredByEditableFilterString = false;
      this.showAdvancedFilters = false;
      this.showMobileFilters = false;
      if (!this.directModeEnabled) {
        localStorage.removeItem(`qx_filters_advanced_${this.nodeId}`);
        localStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      } else {
        sessionStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      }

      this.offset = 0;
      this.currentpage = 1;
      this.updateScroll = false;
      this.getData();
    }
  }

  nullifyFilter(filter: IFilter, event: MouseEvent): void {
    if (filter.displayFormat.includes('DATE') || filter.displayFormat == 'TIME') {
      event.preventDefault();
      filter = this.filterController.clearFilter(filter);
    }

    filter.isNull = true;
    filter.isActive = true;
    filter.updated = true;
    this.buildFilterString();
  }

  clearFilter(filter: IFilter): void {
    filter = this.filterController.clearFilter(filter);
    if (this.filters.filter(f => f.isActive).length == 0) {
      this.pageSize = 20;
      this.filters = this.filterController.clearAllFilters(this.filters);
      this.filterString = '';
      if (!this.directModeEnabled) localStorage.removeItem(`qx_filters_${this.nodeId}`);
      else sessionStorage.removeItem(`qx_filters_${this.nodeId}`);
      this.getData();
      return;
    }

    this.buildFilterString();
  }

  copyValue(filter: IFilter): void {   
    this.app.clipboard.copy(filter.value);
    this.app.alertInfo(this.app.translations.ALERT_Info_Copied);  
  }  

  pasteValueIntoFilter(filter: IFilter): void {
    navigator.clipboard.readText().then(text => {
      filter.value = text;
      filter.isActive = true;
      filter.isNull = false;
      filter.updated = true;
      filter.filteredHistory = filter.history.filter((entry: string) =>
        entry.toLowerCase().includes(text.toLowerCase())
      );
      this.buildFilterString();
    }).catch();
  }

  fromDateUpdated(selectedDate: string, filter: IFilter): void {
    filter.ranges.serverDateFrom = selectedDate;
    filter.ranges.userDateFrom = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);
    if (this.mobileView) {
      filter.isNull = false;
      filter.isActive = true;
    }
  }

  toDateUpdated(selectedDate: string, filter: IFilter): void {
    filter.ranges.serverDateTo = selectedDate;
    filter.ranges.userDateTo = this.filterController.getUserDateFormat(selectedDate, this.settings.dateFormatSetting);
    if (this.mobileView) {
      filter.isNull = false;
      filter.isActive = true;
    }
  }

  createRangeFilter(filter: IFilter): void {
    if (filter.displayFormat == 'TIME' && filter.ranges.from.length == 0 && filter.ranges.to.length == 0) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidDateRange);
      return;
    }

    if (filter.displayFormat != 'TIME' && (filter.ranges.serverDateFrom == '' || filter.ranges.serverDateTo == '' || filter.ranges.serverDateFrom == null || filter.ranges.serverDateTo == null)) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidDateRange);
      return;
    }

    filter.isNull = false;
    filter.isActive = true;
    filter.updated = true;
    this.buildFilterString();
  }

  getFilterTooltip(filter: IFilter): string {
    if (filter.displayFormat == 'TIME') return `${filter.ranges.from} - ${filter.ranges.to}`;
    return `${filter.ranges.userDateFrom} - ${filter.ranges.userDateTo}`;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;

    if (!this.showFilters) {
      this.filters = this.filterController.clearAllFilters(this.filters);
      this.filterString = '';
      if (!this.directModeEnabled)
        localStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      else
        sessionStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: this.filterString, filters: this.filters }));
      this.getData();
    }
  }

  preSelectCheckBoxFilter(filter: IFilter): boolean {
    if (!filter.isActive) return false;
    return this.app.tryParseBoolean(filter.value);
  }

  validateFilterString(filterStringFromInput: string): void {
    this.editableFilterStringError = '';
    let results = this.filterController.formatFilterStringDates(filterStringFromInput, this.filters, this.settings.dateFormatSetting);

    if (results.editableFilterStringError == '') {
      let result = this.filterController.validateFilterString(results.userString, this.headers, false);
      if (result.includes('ERROR')) {
        this.editableFilterStringError = result;
        return;
      }

      this.filteredByEditableFilterString = true;
      localStorage.setItem(`qx_filters_advanced_${this.nodeId}`, JSON.stringify({ userString: results.userString, serverString: results.serverString }));
      localStorage.removeItem(`qx_filters_${this.nodeId}`);
      this.showFilters = false;
      this.offset = 0;
      this.currentpage = 1;
      this.getData();
    } else this.editableFilterStringError = results.editableFilterStringError;
  }

  openAdvancedFilters(): void {
    this.showAdvancedFilters = true;
    this.editableFilterString = this.filterController.createEditableFilterString(this.filters);
  }

  closeAdvancedFilters(): void {
    this.showAdvancedFilters = false;
    this.editableFilterString = '';
    this.filteredByEditableFilterString = false;
    localStorage.removeItem(`qx_filters_advanced_${this.nodeId}`);
    this.filterString = '';
    this.offset = 0;
    this.currentpage = 1;
    this.getData();
  }

  showAdvancedFiltersDialog(): void {
    this.communicationService.showAdvancedFilterDialog(this.filters, this.headers, this.editableFilterString, undefined, this.serverDateFormat);
  }

  focusFilter(filter: IFilter): void {
    filter.focused = !filter.focused;
    filter.showHistory = filter.focused;
    filter.filteredHistory = filter.history;
    this.filters.forEach(f => { if (f != filter) f.focused = false; });
  }

  unFocusAllFilters(): void {
    if (localStorage.getItem(`qx_filters_${this.nodeId}`)) {
      let filters = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}`)).filters;
      let filterString = JSON.parse(localStorage.getItem(`qx_filters_${this.nodeId}`)).filterString;
      filters.forEach(filter => {
        filter.focused = false;
        filter.showHistory = false;
      });
      localStorage.setItem(`qx_filters_${this.nodeId}`, JSON.stringify({ filterString: filterString, filters: filters }));
    }
  }

  @ViewChildren('inputRef') inputRefs!: QueryList<ElementRef<HTMLInputElement>>;
  refocusFilters(): void {
    if (this.filteredByBlur) {
      this.filteredByBlur = false;
      return;
    }

    this.inputRefs.changes.subscribe((refs: QueryList<ElementRef>) => {
      let focusedItemIndex = this.filters.findIndex(item => item.focused);
      if (focusedItemIndex !== -1) {
        this.filters[focusedItemIndex].showHistory = false;
        let input = refs.get(focusedItemIndex);
        input?.nativeElement.focus();
      }
    });
  }
  //#endregion  

  //#region Selection & Expansion
  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent): void {         
    if (event.key == 'F' && event.shiftKey && event.ctrlKey) this.toggleFilters();
    if (event.key == 'E' && event.shiftKey && event.ctrlKey) this.exportData();
    if (event.key == 'Q' && event.shiftKey && event.ctrlKey) this.openColumnDialog();
    if (event.key == 'Z' && event.shiftKey && event.ctrlKey) this.toggleCompactGrid();       
  }

  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent): void {    
    if (event.key == 'Enter') {
      if (this.showFilters) {
        if (this.filters.filter(f => f.isActive).length == 0) {
          this.pageSize = 20;
          this.filters = this.filterController.clearAllFilters(this.filters);
          this.filterString = '';
          if (!this.directModeEnabled) localStorage.removeItem(`qx_filters_${this.nodeId}`);
          else sessionStorage.removeItem(`qx_filters_${this.nodeId}`);
          this.getData();
        } else this.buildFilterString();
      } else if (this.showAdvancedFilters) {
        this.filterString = this.filterInput.nativeElement.value;
        this.validateFilterString(this.filterInput.nativeElement.value);
      }
    }
  }

  selectLines(selectedLine: ILine, event: MouseEvent): void {
    this.filters.forEach(f => f.focused = false);
    selectedLine.selected = !selectedLine.selected;

    if (this.mobileView) {
      if (!selectedLine.selected)
        this.selectedLines = this.selectExpandController.unselectRow(this.selectedLines, selectedLine);
      else
        this.selectedLines.push({ id: selectedLine.rowId, lineData: selectedLine.list, lineNumber: selectedLine.lineNumber });

      this.communicationService.filterRadButtons(this.viewName, this.selectedLines);
      return;
    }

    let result = this.selectExpandController.selectLines(selectedLine, event.shiftKey, event.ctrlKey, this.selectedLines, this.lines, '', '', false);
    this.selectedLines = result.selectedLines;
    this.lines = result.lines;    

    if (this.detailGrid != undefined) {
      this.detailGrid.parentGridSelectedLines = this.selectedLines;
      this.detailGrid.deselectAll();
    }
    this.communicationService.filterRadButtons(this.viewName, this.selectedLines);
  }

  reExpandRows(): void {
    if (this.expandedRows.length == 0) return;
    this.lines = this.selectExpandController.reExpandRows(this.expandedRows, this.lines);
  }

  reSelectRows(): void {
    if (this.selectedLines.length == 0) return;
    this.lines = this.selectExpandController.reSelectRows(this.selectedLines, this.lines);
  }

  deselectAll(): void {
    this.selectedLines = [];
    this.lines.forEach(line => { line.selected = false; })
    this.communicationService.filterRadButtons(this.viewName, this.selectedLines);
  }

  selectAll(): void {
    let result = this.selectExpandController.selectAll(this.lines, this.selectedLines);
    this.selectedLines = result.selectedLines;
    this.lines = result.lines;
    this.communicationService.filterRadButtons(this.viewName, this.selectedLines);
  }

  collapse(expandedLine: ILine): void {
    let result = this.selectExpandController.collapse(expandedLine, this.expandedRows, this.nodeId, undefined, this.lines);
    this.expandedRows = result.expandedRows;
    expandedLine = result.expandedLine;
    this.lines = result.lines;
    this.communicationService.filterRadButtons(this.viewName, this.selectedLines);
  }

  expand(expandedLine: ILine): void {
    this.filters.forEach(f => f.focused = false);
    let res = this.selectExpandController.expand(expandedLine, this.lines, this.expandedRows, undefined, this.nodeId);
    this.lines = res.lines;
    this.expandedRows = res.expandedRows;
    expandedLine = res.expandedLine;
  }  
  //#endregion

  //#region Pagination
  changePageSize(event: any): void {
    this.offset = 0;
    this.currentpage = 1;
    this.pageSize = event.srcElement.value.toString() == this.app.translations.GBL_lbl_AllPageLength ? 50000 : event.target.value;
    this.getData();
  }

  getPageNumber(lineId: string): void {
    let formData = this.app.buildForm(['nodeId', 'userId', 'keyValue'], [this.nodeId, this.userId, lineId]);
    this.gridService.getPageNumber(formData).subscribe({
      next: (data: { result: number }) => {
        if (data) {
          this.currentpage = data.result - 1 == 0 ? 1 : data.result;
          this.offset = this.currentpage == 1 ? 0 : (this.currentpage * this.pageSize) - this.pageSize;
          this.getData();
        }
      }, error: () => {
        this.app.alertError(`Cannot load view via direct link`);//translation needed
        localStorage.removeItem('qx_direct');
        this.loadGrid();
      }
    });
  }

  calculatePageCount(): void {
    this.pageCount = this.totalRecords / this.pageSize;
    if (this.totalRecords % this.pageSize > 0) this.pageCount = this.pageCount + 1;
    this.pageCount = Math.floor(this.pageCount);
  }

  next(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.offset + Number(this.pageSize);
    this.currentpage++;
    this.getData();
  }

  prev(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.offset - Number(this.pageSize);
    if (this.offset < 0) this.offset = 0
    this.currentpage--;
    this.getData();
  }

  first(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = 0;
    this.currentpage = 1;
    this.getData();
  }

  last(): void {
    this.filters.forEach(f => f.focused = false);
    this.offset = this.totalRecords - this.pageSize;
    if (this.offset < 0) this.offset = 0
    this.currentpage = this.pageCount;
    this.getData();
  }

  isFirstPage(): boolean { return this.currentpage == 1; }
  isLastPage(): boolean { return this.currentpage == this.pageCount; }
  //#endregion 

  //#region Misc
  expandImage(image: any): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showImageDialog(this.app.getFormattedImage(image));
  }

  exportData(): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showExportDialog(this.headers, this.lines, 'master', '', this.filterString);
  }

  openColumnDialog(): void {
    this.filters.forEach(f => f.focused = false);
    this.communicationService.showColumnDialog(this.headers, 'master');
  }

  checkIndeterminate(value: any): boolean {
    return typeof value == 'boolean' ? false : true;
  }

  getViewName(): void {
    sessionStorage.setItem('isMaster', 'true');
  }

  @HostListener('window:resize')
  onResize(): void {    
    this.mobileView = window.innerWidth <= 500;
  }

  @ViewChild('contextMenu') contextMenuRef!: ElementRef;
  openContextMenu(event: any, obj: IFilter | IHeader | ILine): void {
    if (this.contextMenuVisible) {
      this.contextMenuVisible = false;
      return;
    }

    event.preventDefault();
    if (obj.kind == 'IFilter') {
      if (obj.displayFormat == 'CHECK' || obj.displayFormat == 'IMAGE') return;
    }

    this.contextMenuTop = -9999;
    this.contextMenuLeft = -9999;
    this.contextMenuVisible = true;
    this.contextMenuObject = obj;

    setTimeout(() => {
      let menu = this.contextMenuRef.nativeElement as HTMLElement;
      let menuWidth = menu.offsetWidth;
      let menuHeight = menu.offsetHeight;
      let viewportWidth = window.innerWidth;
      let viewportHeight = window.innerHeight;

      let left = event.clientX;
      let top = event.clientY;
      if (left + menuWidth > viewportWidth) left = viewportWidth - menuWidth - 10;
      if (top + menuHeight > viewportHeight) top = viewportHeight - menuHeight - 10;
      this.contextMenuLeft = Math.max(0, left);
      this.contextMenuTop = Math.max(0, top);

      switch (obj.kind) {
        case 'IFilter':
          this.contextMenuType = 'filter';
          break;
        case 'IHeader':
          this.contextMenuType = 'header';
          break;
        case 'ILine':
          this.contextMenuType = 'line';
          let temp = this.selectedText;
          this.selectedText = window.getSelection() ? window.getSelection().toString() : temp;
          break;
      }
    }, 0);
  }

  @HostListener('document:click')
  closeContextMenu(): void {
    this.contextMenuVisible = false;
  }

  getHeaderCalculation(header: IHeader, type: string): void {
    let keys = ['userId', 'nodeId', 'viewName', 'filterString', 'keyValue', 'headerField', 'calcType'];
    let values = [this.userId, this.nodeId, 'master', this.filterString, '', header.field, type];
    let formData = this.app.buildForm(keys, values);
    this.gridService.getHeaderCalculation(formData).subscribe({
      next: (data: any) => {
        if (data.result == 'ERR') {
          this.app.alertInfo(this.app.translations.ALERT_Info_InvalidHeaderCalculation);
          return;
        }

        header.appliedCalculationType = type;
        header.appliedCalculationValue = data.result;
        let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
        if (headersWithCalculations.length > 0)
          localStorage.setItem(`qx_calulations_${this.nodeId}`, JSON.stringify(headersWithCalculations));
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  updateHeaderCalculations(headers: IHeader[]): void {
    if (this.i == headers.length) {
      let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
      if (headersWithCalculations.length > 0)
        localStorage.setItem(`qx_calulations_${this.nodeId}`, JSON.stringify(headersWithCalculations));
      return;
    }

    let header = headers[this.i];
    let keys = ['userId', 'nodeId', 'viewName', 'filterString', 'keyValue', 'headerField', 'calcType'];
    let values = [this.userId, this.nodeId, 'master', this.filterString, '', header.field, header.appliedCalculationType];
    let formData = this.app.buildForm(keys, values);
    this.gridService.getHeaderCalculation(formData).subscribe({
      next: (data: any) => {
        if (data.result != 'ERR') header.appliedCalculationValue = data.result;

        if (localStorage.getItem(`qx_calulations_${this.nodeId}`)) {
          let headersWithCalculations = JSON.parse(localStorage.getItem(`qx_calulations_${this.nodeId}`));
          let h = headersWithCalculations.find(h => h.field == header.field);
          if (h) header.appliedCalculationType = h.appliedCalculationType;
        }

        this.i++;
        this.updateHeaderCalculations(headers);
      }, error: (errorLog) => { console.log(errorLog); }
    })
  }

  removeHeaderCalculation(header: IHeader): void {
    header.appliedCalculationType = null;
    header.appliedCalculationValue = null;
    let headersWithCalculations = this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue);
    if (headersWithCalculations.length > 0)
      localStorage.setItem(`qx_calulations_${this.nodeId}`, JSON.stringify(headersWithCalculations));
    else
      localStorage.removeItem(`qx_calulations_${this.nodeId}`);
  }

  getHeaderCalculationsFromStorage(): void {
    let headersWithCalculations = [];
    if (localStorage.getItem(`qx_calulations_${this.nodeId}`)) {
      headersWithCalculations = JSON.parse(localStorage.getItem(`qx_calulations_${this.nodeId}`));
      for (let index = 0; index < headersWithCalculations.length; index++) {
        let h = headersWithCalculations[index];
        let header = this.headers.find(header => header.caption == h.caption);
        header.appliedCalculationType = h.appliedCalculationType;
        header.appliedCalculationValue = h.appliedCalculationValue;
      }
    }

    this.i = 0;
    this.updateHeaderCalculations(this.headers.filter(header => header.appliedCalculationType && header.appliedCalculationValue));
  }

  onScroll(): void {
    if (this.updateScroll && !this.refreshedFromDetail) {
      this.scrollY = this.tabelDiv.nativeElement.scrollTop;
      this.scrollX = this.tabelDiv.nativeElement.scrollLeft;
    }
  }

  resetScrollAfterDetailFiltering(): void {
    if (this.mobileView) return;
    this.tabelDiv.nativeElement.scrollLeft = this.scrollX;
    this.refreshedFromDetail = true;

    setTimeout(() => {
      this.tabelDiv.nativeElement.scrollLeft = this.scrollX;
      this.refreshedFromDetail = false;
      this.updateScroll = true;
    }, 75);
  }

  startLongPress(line: ILine): void {
    this.longPressTimer = setTimeout(() => {
      this.onLongPress(line);
    }, 500);
  }

  cancelLongPress(): void {
    clearTimeout(this.longPressTimer);
  }

  onLongPress(line: ILine) {
    this.contextMenuTop = 50;
    this.contextMenuLeft = 50;
    this.contextMenuType = 'line';
    this.contextMenuVisible = true;
    this.contextMenuObject = line;
  }

  exitDirectMode(): void {
    this.directModeEnabled = false;
    if (sessionStorage.getItem(`qx_filters_${this.nodeId}`))
      sessionStorage.removeItem(`qx_filters_${this.nodeId}`);

    if (sessionStorage.getItem(`qx_sort_${this.nodeId}`))
      sessionStorage.removeItem(`qx_sort_${this.nodeId}`);

    window.location.href = '/base';
  }

  setSelectedLineValueToCopy(text: string): void {
    this.selectedText = text;
  }  

  onDetailGridReady(element: HTMLElement) {
    let container = this.tabelDiv.nativeElement;
    let containerRect = container.getBoundingClientRect();
    let elementRect = element.getBoundingClientRect();    
    let offsetTop = elementRect.top - containerRect.top + container.scrollTop;
    let targetScroll = offsetTop - (container.clientHeight / 2) + (element.clientHeight / 2);
    this.updateScroll = false; 
    container.scrollTo({ top: targetScroll, behavior: 'smooth' });
    setTimeout(() => { this.updateScroll = true; }, 300);
  }
   
  toggleWidgets(): void {
    this.widgetVisible = !this.widgetVisible;
    if (!this.widgetVisible) this.widgetMax = false;
  }

  getSafeTooltip(html: string): string {    
    let sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);
    return sanitized || '';
  } 

  widgetsLoaded(widgets: IWidget[]): void {
    this.widgets = widgets;
  }
  
  toggleMobileTileSize(): void {   
    if (this.mobileTileSize == 'Small') {
      this.mobileTileSize = 'Medium';
      return;
    }
      
    if (this.mobileTileSize == 'Medium') {
      this.mobileTileSize = 'Large';
      return;
    }

    if (this.mobileTileSize == 'Large') {
      this.mobileTileSize = 'Small';
      return;
    }      
  }

  mobileDetailViewLine: ILine|undefined;
  showMobileDetailView: boolean = false;
  openMobileDetailView(line: ILine): void {
    //console.log(line);
    this.mobileDetailViewLine = line;
    this.showMobileDetailView  =true;
  }

  closeDetailView(): void {
    this.showMobileDetailView = false;
    this.mobileDetailViewLine = undefined;
  }
  //#endregion  
}
